import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRememberedSearchParams } from '@/shared/hooks/useRememberedFilters';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { api } from '@/shared/api/axios';
import {
  Target,
  LayoutGrid,
  List,
  Search,
  Plus,
  Download,
  RotateCcw,
  IndianRupee,
  Layers,
  Award,
  BarChart3,
  Calendar,
  Building2,
  Users,
  Phone,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Sparkles,
} from 'lucide-react';

import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatINR, formatMoney } from '@/shared/utils/currency';
import { useForexRates } from '@/shared/hooks/useForex';
import { cn, localToday } from '@/shared/utils/utils';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { useUser } from '@/shared/hooks/useAuth';

import {
  usePipelineList,
  usePipelineStages,
  usePipelineRepSummary,
  usePipelineMutations,
  stageErrorText,
} from '../hooks/usePipeline';
import { EmployeePipelineMonitor } from '../components/EmployeePipelineMonitor';
import { userName, leadNumberLabel, type PipelineRecord, type PipelineSort } from '../types';

/** Columns the backend allowlists for sorting (PIPELINE_SORTS, src/api-pipeline/index.js). */
type PipelineSortColumn = 'name' | 'value' | 'date' | 'leadno';
const PIPELINE_SORT_ASC: Record<PipelineSortColumn, PipelineSort> = { name: 'name_asc', value: 'value_asc', date: 'date_asc', leadno: 'leadno_asc' };
const PIPELINE_SORT_DESC: Record<PipelineSortColumn, PipelineSort> = { name: 'name_desc', value: 'value_desc', date: 'date_desc', leadno: 'leadno_desc' };
import { LostReasonModal } from '@/features/deals/components/LostReasonModal';
import { StageRegressionNoteModal } from '../components/StageRegressionNoteModal';
import type { Deal } from '@/features/deals/types';

const asDeal = (r: PipelineRecord) => r as unknown as Deal;

type View = 'board' | 'table';
type DatePreset = 'all' | 'today' | 'yesterday' | 'week' | 'month' | 'custom';

// Client-directed policy: Super Admin is the only tier that browses the whole
// pipeline. Everyone else — admin and department_admin included — sees
// strictly their own created-or-assigned records, matching the server-side
// rule in src/api-pipeline/index.js's scopeFilter (that IS the actual
// enforcement; this only decides whether the UI still offers a picker that
// would otherwise ask for data the server won't return).
//
// The unclaimed pool from every source (IndiaMART, JustDial, TradeIndia,
// Facebook, website, manual accounts handover) lives on its own screen —
// Incoming Leads, under Operations — not as a second mode of this page.
// Claiming one there is what puts it into a person's Sales Pipeline; this
// page has no "browse what nobody has claimed yet" view of its own.
const PIPELINE_ADMIN_ROLES = new Set(['superadmin', 'admin', 'ceo', 'owner', 'department_admin']);

export function SalesPipelinePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useUser();
  const isPipelineAdmin = Boolean(currentUser && PIPELINE_ADMIN_ROLES.has(currentUser.role));
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const [view, setView] = useState<View>('table');
  // Filters stay put until reset (client, 2026-10-03): opening a record and
  // coming back — even via the sidebar, with a bare /pipeline URL — restores
  // the filters this user last left here. A link that carries its own filters
  // (a dashboard tile) still wins. beforeStage is a one-off link parameter.
  const { seed, remember } = useRememberedSearchParams('sales-pipeline', searchParams, ['beforeStage']);
  // The rest of this filter bar now seeds from, and writes back to, the same
  // URL beforeStage already used — so a shared link, a refresh, or Back
  // reopens the same filtered view instead of resetting to defaults.
  const [search, setSearch] = useState(() => seed.get('search') || '');
  const [stage, setStage] = useState(() => seed.get('stage') || '');
  // Set only by an incoming link (the dashboard's Open Enquiries tile is the
  // one example today: /pipeline?beforeStage=order_confirmed), never by a
  // control on this page — so it lives outside the ordinary filter state and
  // is cleared by leaving the page, not by anything the filter bar resets.
  const [beforeStage, setBeforeStage] = useState(() => searchParams.get('beforeStage') || '');
  const [source, setSource] = useState(() => seed.get('source') || '');
  const [owner, setOwner] = useState(() => seed.get('owner') || '');
  // Admin-tier only (see the toggle below) — isolates records with an open
  // cross-BDM reassignment request awaiting a decision.
  const [pendingReassignmentOnly, setPendingReassignmentOnly] = useState(() => seed.get('pendingReassignment') === '1');
  // Only meaningful — and only shown — alongside stage=closed_lost.
  const [lostReason, setLostReason] = useState(() => seed.get('lostReason') || '');
  const [datePreset, setDatePreset] = useState<DatePreset>(() => (seed.get('datePreset') as DatePreset) || 'all');
  const [customStartDate, setCustomStartDate] = useState(() => seed.get('startDate') || '');
  const [customEndDate, setCustomEndDate] = useState(() => seed.get('endDate') || '');
  const [sort, setSort] = useState<PipelineSort>(() => (seed.get('sort') as PipelineSort) || 'recent');

  // Mirror the live filters back into the URL (replacing, not pushing, so
  // Back doesn't step through every keystroke). beforeStage keeps its own
  // dedicated clearBeforeStage handling below and isn't re-derived here.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, value: string | undefined | false) => {
      if (value) next.set(key, String(value)); else next.delete(key);
    };
    setOrDelete('search', search.trim() || undefined);
    setOrDelete('stage', stage || undefined);
    setOrDelete('source', source || undefined);
    setOrDelete('owner', owner || undefined);
    setOrDelete('pendingReassignment', pendingReassignmentOnly ? '1' : undefined);
    setOrDelete('lostReason', stage === 'closed_lost' ? (lostReason || undefined) : undefined);
    setOrDelete('datePreset', datePreset !== 'all' ? datePreset : undefined);
    setOrDelete('startDate', datePreset === 'custom' ? (customStartDate || undefined) : undefined);
    setOrDelete('endDate', datePreset === 'custom' ? (customEndDate || undefined) : undefined);
    setOrDelete('sort', sort !== 'recent' ? sort : undefined);
    setSearchParams(next, { replace: true });
    remember(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, stage, source, owner, pendingReassignmentOnly, lostReason, datePreset, customStartDate, customEndDate, sort]);

  const toggleSort = (column: PipelineSortColumn) => {
    setSort((cur) => (cur === PIPELINE_SORT_DESC[column] ? PIPELINE_SORT_ASC[column] : PIPELINE_SORT_DESC[column]));
  };
  const sortIcon = (column: PipelineSortColumn) => {
    if (sort === PIPELINE_SORT_ASC[column]) return <ArrowUp className="w-3 h-3" />;
    if (sort === PIPELINE_SORT_DESC[column]) return <ArrowDown className="w-3 h-3" />;
    return <ArrowUpDown className="w-3 h-3 opacity-30" />;
  };

  // Fetch assignable employees / users
  const { data: userOptions = [] } = useUserOptions();

  // Same query LostReasonModal already uses to capture a reason at close-lost
  // time — reused here, not re-fetched from a different master-data endpoint,
  // so the filter always offers exactly the reasons a record could actually
  // have been closed with.
  const { data: lostReasonData } = useQuery({
    queryKey: ['deals', 'lost-reasons'],
    queryFn: async () => (await api.get('/api/deals-outcome/lost-reasons')).data as { items?: { _id: string; name: string }[] },
  });
  const lostReasonOptions = lostReasonData?.items || [];

  // Compute date range from preset
  const { startDate, endDate } = useMemo(() => {
    if (datePreset === 'all') return { startDate: undefined, endDate: undefined };
    const now = new Date();

    if (datePreset === 'today') {
      const d = now.toISOString().split('T')[0];
      return { startDate: d, endDate: d };
    }
    if (datePreset === 'yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const d = y.toISOString().split('T')[0];
      return { startDate: d, endDate: d };
    }
    if (datePreset === 'week') {
      const w = new Date(now);
      w.setDate(w.getDate() - 7);
      return { startDate: w.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
    }
    if (datePreset === 'month') {
      const m = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: m.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
    }
    if (datePreset === 'custom') {
      return {
        startDate: customStartDate || undefined,
        endDate: customEndDate || undefined,
      };
    }
    return { startDate: undefined, endDate: undefined };
  }, [datePreset, customStartDate, customEndDate]);

  // Non-superadmin defaults the owner filter to the logged-in user and locks
  // it there (see the Owner <Select> below) — that IS the "just my assigned
  // stuff" restriction. It's also redundant with the server's own scopeFilter
  // at this point (which returns only the caller's records regardless of what
  // `owner` asks for), but sending it keeps the two pagination/KPI numbers
  // honest without relying on the reader to know that.
  const effectiveOwner = isPipelineAdmin ? (owner || undefined) : currentUser?.id;

  const filters = useMemo(() => ({
    search: search.trim() || undefined,
    stage: stage || undefined,
    lostReason: stage === 'closed_lost' ? (lostReason || undefined) : undefined,
    source: source || undefined,
    owner: effectiveOwner,
    startDate,
    endDate,
    beforeStage: beforeStage || undefined,
    sort,
    limit: 200,
    offset: 0,
  }), [search, stage, lostReason, source, effectiveOwner, startDate, endDate, beforeStage, isPipelineAdmin, isSuperAdmin, pendingReassignmentOnly, sort]);

  const { data: stageData, isLoading: stagesLoading } = usePipelineStages({
    owner: effectiveOwner,
    startDate,
    endDate,
  });
  const { data: repSummaryData, isLoading: repSummaryLoading } = usePipelineRepSummary(
    { startDate, endDate },
    isPipelineAdmin
  );
  const { data, isLoading } = usePipelineList(filters);
  const { moveStage } = usePipelineMutations();

  const [pendingLostMove, setPendingLostMove] = useState<PipelineRecord | null>(null);
  const [pendingRegression, setPendingRegression] = useState<{ record: PipelineRecord; toStage: string } | null>(null);

  const records = data?.items ?? [];
  const stages = stageData?.stages ?? [];

  const totalStagesCount = useMemo(() => {
    if (typeof stageData?.total === 'number') return stageData.total;
    if (typeof stageData?.totals?.all === 'number') return stageData.totals.all;
    return stages.reduce((sum, s) => sum + (s.count || 0), 0);
  }, [stageData, stages]);

  const stageOptions = useMemo(() => {
    return stages.map((s) => ({
      value: s.key,
      label: `${s.label} (${s.count})`,
    }));
  }, [stages]);

  const byStage = useMemo(() => {
    const m = new Map<string, PipelineRecord[]>();
    for (const r of records) {
      const list = m.get(r.stage) || [];
      list.push(r);
      m.set(r.stage, list);
    }
    return m;
  }, [records]);

  const stageLabel = useMemo(
    () => Object.fromEntries(stages.map((s) => [s.key, s.label])),
    [stages],
  );

  const clearBeforeStage = () => {
    setBeforeStage('');
    const next = new URLSearchParams(searchParams);
    next.delete('beforeStage');
    setSearchParams(next, { replace: true });
  };

  // Position within the server's ordered stage list — used only to detect a
  // BACKWARD move client-side, so the UI can ask for a note before submitting
  // rather than after a 400 comes back. closed_lost sits at the end of the
  // list (highest ordinal) and is excluded — it already has its own reason
  // flow and is never treated as a "regression" for this purpose, matching
  // validateManualStageChange in src/pipeline-policy.js.
  const stageOrdinal = useMemo(
    () => Object.fromEntries(stages.map((s, i) => [s.key, i])),
    [stages],
  );

  // ── Metric Calculations ───────────────────────────────────────────────────
  // A USD/EUR record's value is in that currency; totals are in ₹, so foreign
  // values are converted at the live rate (and left out until it has loaded).
  const foreignCodes = useMemo(
    () => [...new Set(records.map((r) => String(r.currency || 'INR').toUpperCase()).filter((c) => c !== 'INR'))].sort(),
    [records],
  );
  const { data: fx } = useForexRates('INR', foreignCodes.join(',') || 'USD');
  const toInr = useMemo(() => {
    // Each rate in fx has inrRate (1 foreign unit = X INR) or rate (foreign per 1 INR)
    const inrPerUnit = new Map((fx?.rates || []).map((x: any) => [
      x.quote.toUpperCase(),
      Number(x.inrRate || (x.rate > 0 ? 1 / x.rate : 0)),
    ]));
    return (r: PipelineRecord) => {
      const v = Number(r.value) || 0;
      const code = String(r.currency || 'INR').toUpperCase();
      if (code === 'INR') return v;
      const rate = inrPerUnit.get(code);
      // Foreign Price * ExchangeRate (1 Foreign Unit = X INR)
      return rate && rate > 0 ? Math.round(v * rate * 100) / 100 : 0;
    };
  }, [fx]);

  const metrics = useMemo(() => {
    const activeRecords = records.filter((r) => r.stage !== 'closed_lost');
    const wonRecords = records.filter((r) => r.stage === 'closed_won');
    const lostRecords = records.filter((r) => r.stage === 'closed_lost');
    const openRecords = records.filter((r) => r.stage !== 'closed_won' && r.stage !== 'closed_lost');

    const openPipelineValue = openRecords.reduce((sum, r) => sum + toInr(r), 0);
    const wonValue = wonRecords.reduce((sum, r) => sum + toInr(r), 0);
    const totalPipelineValue = activeRecords.reduce((sum, r) => sum + toInr(r), 0);

    const openValuedRecords = openRecords.filter((r) => Number(r.value) > 0);
    const avgDealSize = openValuedRecords.length > 0
      ? Math.round(openPipelineValue / openValuedRecords.length)
      : (activeRecords.filter((r) => Number(r.value) > 0).length > 0
        ? Math.round(totalPipelineValue / activeRecords.filter((r) => Number(r.value) > 0).length)
        : 0);

    const totalClosed = wonRecords.length + lostRecords.length;
    const winRate = totalClosed > 0 ? Math.round((wonRecords.length / totalClosed) * 100) : 0;

    return {
      openPipelineValue,
      totalPipelineValue,
      activeCount: activeRecords.length,
      openCount: openRecords.length,
      wonCount: wonRecords.length,
      wonValue,
      avgDealSize,
      winRate,
      lostCount: lostRecords.length,
    };
  }, [records, toInr]);

  // Check if any filter is active
  const hasActiveFilters = Boolean(
    search.trim() ||
    stage ||
    lostReason ||
    source ||
    owner ||
    datePreset !== 'all' ||
    customStartDate ||
    customEndDate
  );

  const resetFilters = () => {
    setSearch('');
    setStage('');
    setLostReason('');
    setSource('');
    setOwner('');
    setDatePreset('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setPendingReassignmentOnly(false);
    toast.success('Filters reset to default');
  };

  // Picking a stage other than Closed Lost makes the reason filter both
  // meaningless and hidden — clear it so it can't linger unseen and keep
  // narrowing a later, unrelated stage selection.
  const setStageFiltered = (next: string) => {
    setStage(next);
    if (next !== 'closed_lost') setLostReason('');
  };

  // ── Export CSV Handler ────────────────────────────────────────────────────
  const exportCSV = () => {
    if (records.length === 0) {
      toast.error('No records available to export.');
      return;
    }

    const headers = [
      'Lead No.',
      'Record ID',
      'Contact Name',
      'Company Name',
      'Stage',
      'Assigned Owner',
      'Source',
      'Expected Value (INR)',
      'Phone Number',
      'Email',
      'Created Date & Time',
      'Items Count',
      'Notes',
    ];

    const rows = records.map((r) => [
      leadNumberLabel(r.lead_no),
      r._id || r.id,
      `"${(r.name || r.title || '').replace(/"/g, '""')}"`,
      `"${(r.business_name || '').replace(/"/g, '""')}"`,
      `"${(stageLabel[r.stage] || r.stage || '').replace(/"/g, '""')}"`,
      `"${userName(r.assigned_to ?? r.user_id).replace(/"/g, '""')}"`,
      `"${(r.source || 'direct').replace(/"/g, '""')}"`,
      r.value ?? 0,
      `"${(r.phone || '').replace(/"/g, '""')}"`,
      `"${(r.email || '').replace(/"/g, '""')}"`,
      r.created_at ? `"${new Date(r.created_at).toLocaleString('en-IN')}"` : '',
      r.lines?.length ?? 0,
      `"${(r.notes || '').replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStr = localToday();
    link.setAttribute('href', url);
    link.setAttribute('download', `sales_pipeline_export_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${records.length} records to CSV.`);
  };

  const submitMove = (record: PipelineRecord, toStage: string, extra: { lostReasonId?: string; lostReasonNote?: string; moveNote?: string } = {}) => {
    moveStage.mutate(
      { id: record._id, stage: toStage, ...extra },
      {
        onSuccess: (r) => {
          if (!r.ok) {
            toast.error(r.error || 'Could not move the record.');
            return;
          }
          setPendingLostMove(null);
          setPendingRegression(null);
          if (!r.changed) return;
          toast.success(`Moved to ${stageLabel[toStage] || toStage}`);
        },
        onError: (err) => toast.error(stageErrorText(err)),
      },
    );
  };

  const onMove = (record: PipelineRecord, toStage: string) => {
    if (toStage === record.stage) return;

    if (toStage === 'closed_lost') {
      setPendingLostMove(record);
      return;
    }

    const fromOrdinal = stageOrdinal[record.stage];
    const toOrdinal = stageOrdinal[toStage];
    const isBackward = fromOrdinal !== undefined && toOrdinal !== undefined && toOrdinal < fromOrdinal;
    if (isBackward) {
      setPendingRegression({ record, toStage });
      return;
    }

    submitMove(record, toStage);
  };

  return (
    <div className="mx-auto flex w-full max-w-[1580px] flex-col gap-5 pb-16">
      <PageHeader
        icon={Target}
        eyebrow="Sales"
        title="Sales Pipeline"
        description={isPipelineAdmin
          ? 'Every enquiry, walk-in and deal across the team, from first contact to closed.'
          : 'Enquiries and deals you created or were assigned. Claim more from Incoming Leads.'}
        actions={<>
          <Button variant="outline" onClick={exportCSV} disabled={records.length === 0}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button onClick={() => navigate('/pipeline/new')}>
            <Plus className="h-4 w-4" /> New Pipeline Record
          </Button>
        </>}
      />

      {/* Arrived from the dashboard's Open Enquiries tile — visible so the
          filtered list doesn't look like an unexplained slice of the pipeline,
          and clearable so it doesn't quietly follow the rep around the page. */}
      {beforeStage && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2.5">
          <span className="text-body-sm font-medium text-on-surface">
            Showing Open Enquiries — every stage before{' '}
            <strong>{stageLabel[beforeStage] || beforeStage}</strong>.
          </span>
          <button
            type="button"
            onClick={clearBeforeStage}
            className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-body-xs font-semibold text-primary hover:bg-primary/10"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        </div>
      )}

      {/* ── Employee Pipeline Monitor (Super Admin, Admin, Dept Admin) ───────── */}
      {isPipelineAdmin && (
        <EmployeePipelineMonitor
          summaryData={repSummaryData}
          isLoading={repSummaryLoading}
          selectedOwnerId={owner}
          onSelectOwner={(ownerId) => setOwner(ownerId)}
          userRole={currentUser?.role}
        />
      )}

      {/* ── Summary metrics ─────────────────────────────────────────────── */}
      <StatGrid cols={4}>
        <StatCard label="Pipeline Value" value={formatINR(metrics.openPipelineValue)} hint={`Across ${metrics.openCount} open ${metrics.openCount === 1 ? 'opportunity' : 'opportunities'}`} icon={IndianRupee} tone="primary" />
        <StatCard label="In Pipeline" value={metrics.openCount} hint={`Open enquiries · ${records.length} ${records.length === 1 ? 'record' : 'records'} in view`} icon={Layers} tone="info" />
        <StatCard label="Deals Won" value={metrics.wonCount} hint={`${formatINR(metrics.wonValue)} · ${metrics.winRate}% win rate`} icon={Award} tone="success" />
        <StatCard label="Avg. Deal Size" value={formatINR(metrics.avgDealSize)} hint="Average expected open deal value" icon={BarChart3} tone="warning" />
      </StatGrid>

      {/* ── Filters Toolbar ────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 rounded-card border border-border bg-card p-4 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Search */}
          <div className="relative min-w-[14rem] flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company or phone…"
              className="h-10 w-full rounded-xl border border-outline bg-surface pl-10 pr-3 text-body-sm text-on-surface focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition"
            />
          </div>

          {/* Quick status tabs: All, In Pipeline, Won, Lost */}
          <div className="inline-flex items-center gap-1 rounded-xl border border-outline p-1 bg-surface-variant/30 text-body-xs font-semibold">
            <button
              onClick={() => setStageFiltered('')}
              className={cn(
                'px-2.5 py-1 rounded-lg transition',
                !stage ? 'bg-surface text-primary shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
              )}
            >
              All ({records.length})
            </button>
            <button
              onClick={() => setStageFiltered('open')}
              className={cn(
                'px-2.5 py-1 rounded-lg transition',
                stage === 'open' ? 'bg-surface text-primary shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
              )}
            >
              In Pipeline ({metrics.openCount})
            </button>
            <button
              onClick={() => setStageFiltered('closed_won')}
              className={cn(
                'px-2.5 py-1 rounded-lg transition',
                stage === 'closed_won' ? 'bg-surface text-success-text shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
              )}
            >
              Won ({metrics.wonCount})
            </button>
            <button
              onClick={() => setStageFiltered('closed_lost')}
              className={cn(
                'px-2.5 py-1 rounded-lg transition',
                stage.startsWith('closed_lost') ? 'bg-surface text-danger-text shadow-xs' : 'text-on-surface-variant hover:text-on-surface'
              )}
            >
              Lost ({metrics.lostCount})
            </button>
          </div>

          {/* When "Lost" is selected then only display the filter that too only closed lost filter option there */}
          {stage.startsWith('closed_lost') && (
            <div className="w-56">
              <Select
                value={stage}
                onChange={(e) => setStageFiltered(e.target.value)}
                options={[
                  { value: 'closed_lost', label: 'Closed Lost (All)' },
                  { value: 'closed_lost:price_issue', label: 'Closed Lost: Price Issue' },
                  { value: 'closed_lost:poor_quality', label: 'Closed Lost: Poor Quality' },
                  { value: 'closed_lost:other', label: 'Closed Lost: Other Issues' },
                ]}
              />
            </div>
          )}

          {/* View Toggle */}
          <div className="inline-flex rounded-xl border border-outline p-1 bg-surface-variant/30">
            <IconToggle active={view === 'board'} onClick={() => setView('board')} label="Board View">
              <LayoutGrid className="h-4 w-4" />
              <span className="text-body-xs font-medium">Board</span>
            </IconToggle>
            <IconToggle active={view === 'table'} onClick={() => setView('table')} label="Table View">
              <List className="h-4 w-4" />
              <span className="text-body-xs font-medium">Table</span>
            </IconToggle>
          </div>
        </div>

        {/* Filter Dropdowns Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 pt-1 border-t border-outline/50">
          {/* Stage Filter */}
          <Select
            value={stage}
            onChange={(e) => setStageFiltered(e.target.value)}
            options={[
              { value: '', label: `All Stages (${totalStagesCount})` },
              ...stageOptions,
            ]}
          />


          {/* Lost Reason — only meaningful, and only shown, once Closed Lost
              is the selected stage; a record only ever has a lostReasonId
              once it's actually been closed lost. */}
          {stage === 'closed_lost' && (
            <Select
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              options={[
                { value: '', label: 'All Lost Reasons' },
                ...lostReasonOptions.map((r) => ({ value: r._id, label: r.name })),
              ]}
            />
          )}

          {/* Source Filter */}
          <Select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            options={[
              { value: '', label: 'All Sources' },
              { value: 'direct', label: 'Direct / Walk-in' },
              { value: 'indiamart', label: 'IndiaMART Lead' },
              { value: 'referral', label: 'Referral' },
              { value: 'cold_call', label: 'Cold Call' },
              { value: 'exhibition', label: 'Exhibition' },
              { value: 'website', label: 'Website / Inbound' },
              { value: 'other', label: 'Other' },
            ]}
          />

          {/* Employee / Owner Filter — only an admin tier browses other
             people's pipelines; everyone else is locked to their own (see
             effectiveOwner above), so the picker would just be a control that
             does nothing for them. */}
          {isPipelineAdmin && (
          <Select
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            options={[
              { value: '', label: 'All Employees' },
              ...userOptions.map((u) => ({
                value: u.id,
                label: `${u.display_name} (${u.department || 'Sales'})`,
              })),
            ]}
          />
          )}

          {/* Old reassignment requests — tracking for the Super Admin only;
              approval is bypassed, so nothing waits on them. */}
          {isSuperAdmin && (
            <label className="flex items-center gap-1.5 text-body-sm text-on-surface-variant cursor-pointer select-none">
              <input
                type="checkbox"
                checked={pendingReassignmentOnly}
                onChange={(e) => setPendingReassignmentOnly(e.target.checked)}
                className="rounded border-outline"
              />
              Old reassignment requests
            </label>
          )}

          {/* Date Range Preset */}
          <Select
            value={datePreset}
            onChange={(e) => setDatePreset(e.target.value as DatePreset)}
            options={[
              { value: 'all', label: 'Date: All Time' },
              { value: 'today', label: 'Date: Today' },
              { value: 'yesterday', label: 'Date: Yesterday' },
              { value: 'week', label: 'Date: Last 7 Days' },
              { value: 'month', label: 'Date: This Month' },
              { value: 'custom', label: 'Date: Custom Range' },
            ]}
          />

          {/* Reset Filters Action */}
          <div className="flex items-center">
            <Button
              type="button"
              variant="ghost"
              onClick={resetFilters}
              disabled={!hasActiveFilters}
              className={cn(
                'w-full justify-center rounded-xl h-10 transition',
                hasActiveFilters
                  ? 'text-primary font-semibold hover:bg-primary/10'
                  : 'text-on-surface-variant/50 cursor-not-allowed'
              )}
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Reset Filters
            </Button>
          </div>
        </div>

        {/* Custom Date Pickers (if Custom Range selected) */}
        {datePreset === 'custom' && (
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-outline/50 animate-in fade-in duration-200">
            <div className="flex items-center gap-2 text-body-xs text-on-surface-variant font-medium">
              <Calendar className="h-4 w-4 text-primary" /> Custom Range:
            </div>
            <div className="flex items-center gap-2">
              <label className="text-body-xs text-on-surface-variant">From:</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="h-9 rounded-lg border border-outline bg-surface px-2.5 text-body-xs text-on-surface focus:border-primary focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-body-xs text-on-surface-variant">To:</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="h-9 rounded-lg border border-outline bg-surface px-2.5 text-body-xs text-on-surface focus:border-primary focus:outline-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Content View ────────────────────────────────────────────────────── */}
      {isLoading || stagesLoading ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : records.length === 0 ? (
        <EmptyState
          title="No records found"
          description={
            hasActiveFilters
              ? 'No pipeline records match the selected filters. Try resetting the filters.'
              : 'Claim an enquiry from Incoming Leads, or create one for a walk-in.'
          }
          action={
            hasActiveFilters ? (
              <Button variant="outline" onClick={resetFilters}>
                <RotateCcw className="mr-1.5 h-4 w-4" /> Reset Filters
              </Button>
            ) : (
              <Button onClick={() => navigate('/pipeline/new')}>
                <Plus className="mr-1.5 h-4 w-4" /> Create a Record
              </Button>
            )
          }
        />
      ) : view === 'board' ? (
        <Board
          stages={stages}
          byStage={byStage}
          toInr={toInr}
          onOpen={(r) => navigate(`/pipeline/${r._id}`)}
          onMove={onMove}
        />
      ) : (
        <TableView
          records={records}
          stageLabel={stageLabel}
          onOpen={(r) => navigate(`/pipeline/${r._id}`)}
          onSort={toggleSort}
          sortIcon={sortIcon}
        />
      )}

      {pendingLostMove && (
        <LostReasonModal
          deal={asDeal(pendingLostMove)}
          isSaving={moveStage.isPending}
          onCancel={() => setPendingLostMove(null)}
          onConfirm={(payload) => submitMove(pendingLostMove, 'closed_lost', payload)}
        />
      )}

      {pendingRegression && (
        <StageRegressionNoteModal
          recordName={pendingRegression.record.name || pendingRegression.record.title || 'this record'}
          fromLabel={stageLabel[pendingRegression.record.stage] || pendingRegression.record.stage}
          toLabel={stageLabel[pendingRegression.toStage] || pendingRegression.toStage}
          isSaving={moveStage.isPending}
          onCancel={() => setPendingRegression(null)}
          onConfirm={(moveNote) => submitMove(pendingRegression.record, pendingRegression.toStage, { moveNote })}
        />
      )}
    </div>
  );
}

// ─── Board (Kanban) View ─────────────────────────────────────────────────────

function Board({
  stages,
  byStage,
  toInr,
  onOpen,
  onMove,
}: {
  stages: Array<{ key: string; label: string; count: number }>;
  byStage: Map<string, PipelineRecord[]>;
  toInr: (r: PipelineRecord) => number;
  onOpen: (r: PipelineRecord) => void;
  onMove: (r: PipelineRecord, stage: string) => void;
}) {
  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex min-w-max gap-3.5">
        {stages.map((s) => {
          const items = byStage.get(s.key) ?? [];
          const columnTotal = items.reduce((sum, item) => sum + toInr(item), 0);

          return (
            <section
              key={s.key}
              className="flex w-80 shrink-0 flex-col gap-2.5 rounded-card border border-border bg-muted/50 p-2.5"
            >
              <header className="flex items-center justify-between rounded-card-sm border border-border bg-card px-3 py-2 shadow-xs">
                <div className="flex items-center gap-2">
                  <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">{s.label}</span>
                  <Badge tone="neutral" className="text-body-xs font-semibold">
                    {items.length}
                  </Badge>
                </div>
                {columnTotal > 0 && (
                  <span className="font-mono tabular-nums text-right text-data-sm font-semibold text-on-surface">
                    {formatINR(columnTotal)}
                  </span>
                )}
              </header>

              <div className="flex flex-col gap-2.5">
                {items.map((r) => (
                  <RecordCard
                    key={r._id}
                    record={r}
                    stages={stages}
                    onOpen={() => onOpen(r)}
                    onMove={(stage) => onMove(r, stage)}
                  />
                ))}
                {items.length === 0 && (
                  <p className="rounded-card-sm border border-dashed border-outline-variant bg-card/60 px-3 py-8 text-center text-body-xs text-on-surface-variant">
                    Nothing at this stage yet. Move a record here with the Stage selector on its card.
                  </p>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function RecordCard({
  record,
  stages,
  onOpen,
  onMove,
}: {
  record: PipelineRecord;
  stages: Array<{ key: string; label: string }>;
  onOpen: () => void;
  onMove: (stage: string) => void;
}) {
  const isSuperAdmin = useUser()?.role === 'superadmin';
  return (
    <article
      onClick={onOpen}
      className="group cursor-pointer rounded-card border border-border bg-card p-3.5 transition hover:border-primary/50 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-body-sm text-on-surface group-hover:text-primary transition line-clamp-1">
          {record.name || record.title}
        </p>
        {record.source && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-on-surface-variant shrink-0">
            {record.source}
          </span>
        )}
      </div>

      <SharedChip record={record} />

      {isSuperAdmin && record.reassignmentRequest?.status === 'pending' && (
        <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-on-surface-variant border border-border">
          Old reassignment request
        </span>
      )}

      {record.business_name && record.business_name !== record.name && (
        <p className="mt-0.5 truncate text-body-xs text-on-surface-variant flex items-center gap-1">
          <Building2 className="h-3 w-3 shrink-0" />
          {record.business_name}
        </p>
      )}
      {record.existing_customer && (
        <span
          className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-900"
          title={`Customer already in the CRM${record.related_owner_name ? ` — earlier record with ${record.related_owner_name}` : ''}${record.related_stage ? ` (${record.related_stage.replace(/_/g, ' ')})` : ''}`}
        >
          Existing customer
        </span>
      )}

      <div className="mt-2 flex items-baseline justify-between gap-2 border-t border-border pt-2">
        <span className="font-mono tabular-nums text-body-sm font-semibold text-on-surface">
          {record.value ? formatMoney(record.value, record.currency) : '—'}
        </span>
        <span className="truncate text-data-xs text-on-surface-variant">
          {userName(record.assigned_to ?? record.user_id)}
        </span>
      </div>

      {/* Stage display and Close Lost action */}
      <div onClick={(e) => e.stopPropagation()} className="mt-2.5 flex items-center justify-between gap-2 border-t border-border/60 pt-2">
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-on-surface-variant truncate">
          <span className="h-1.5 w-1.5 rounded-full bg-primary/70 shrink-0" />
          {stages.find((s) => s.key === record.stage)?.label || record.stage}
        </span>
        {record.stage !== 'closed_lost' && record.stage !== 'closed_won' && (
          <button
            type="button"
            onClick={() => onMove('closed_lost')}
            className="shrink-0 text-data-xs font-semibold text-danger-text hover:underline"
            title="Mark this lead as lost, irrelevant, or not needed"
          >
            Close Lost
          </button>
        )}
      </div>
    </article>
  );
}

// ─── Table View ──────────────────────────────────────────────────────────────

function TableView({
  records,
  stageLabel,
  onOpen,
  onSort,
  sortIcon,
}: {
  records: PipelineRecord[];
  stageLabel: Record<string, string>;
  onOpen: (r: PipelineRecord) => void;
  onSort: (column: PipelineSortColumn) => void;
  sortIcon: (column: PipelineSortColumn) => React.ReactNode;
}) {
  const isSuperAdmin = useUser()?.role === 'superadmin';
  return (
    <div className="overflow-hidden rounded-card border border-border bg-card shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[68rem] text-left text-body-sm">
          <thead className="border-b border-border bg-muted text-label-sm uppercase tracking-wider text-on-surface-variant">
            <tr>
              <th
                className="sticky top-0 z-10 bg-muted px-4 py-3 cursor-pointer select-none whitespace-nowrap hover:text-on-surface"
                onClick={() => onSort('leadno')}
              >
                <span className="inline-flex items-center gap-1">Lead No. {sortIcon('leadno')}</span>
              </th>
              <th
                className="sticky top-0 z-10 bg-muted px-4 py-3 cursor-pointer select-none hover:text-on-surface"
                onClick={() => onSort('name')}
              >
                <span className="inline-flex items-center gap-1">Client & Company {sortIcon('name')}</span>
              </th>
              <th
                className="sticky top-0 z-10 bg-muted px-4 py-3 cursor-pointer select-none whitespace-nowrap hover:text-on-surface"
                onClick={() => onSort('date')}
              >
                <span className="inline-flex items-center gap-1">Created {sortIcon('date')}</span>
              </th>
              <th scope="col" className="sticky top-0 z-10 bg-muted px-4 py-3">Stage</th>
              <th scope="col" className="sticky top-0 z-10 bg-muted px-4 py-3">Owner</th>
              <th scope="col" className="sticky top-0 z-10 bg-muted px-4 py-3">Source</th>
              <th scope="col" className="sticky top-0 z-10 bg-muted px-4 py-3">Contact</th>
              <th
                className="sticky top-0 z-10 bg-muted px-4 py-3 text-right cursor-pointer select-none hover:text-on-surface"
                onClick={() => onSort('value')}
              >
                <span className="inline-flex items-center justify-end gap-1">Expected Value {sortIcon('value')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {records.map((r) => (
              <tr
                key={r._id}
                onClick={() => onOpen(r)}
                className="cursor-pointer transition hover:bg-surface-variant/40"
              >
                <td className="px-4 py-3 whitespace-nowrap font-mono text-body-xs font-semibold text-on-surface-variant">
                  {leadNumberLabel(r.lead_no) || '—'}
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-on-surface">{r.name || r.title}</p>
                  {r.business_name && r.business_name !== r.name && (
                    <p className="text-body-xs text-on-surface-variant">{r.business_name}</p>
                  )}
                  <SharedChip record={r} />
                  {isSuperAdmin && r.reassignmentRequest?.status === 'pending' && (
                    <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-data-xs font-bold text-on-surface-variant border border-border">
                      Old reassignment request
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-body-xs text-on-surface-variant">
                  {r.created_at ? (
                    <>
                      <span className="block text-on-surface">{new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      {new Date(r.created_at).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}
                    </>
                  ) : '—'}
                </td>
                <td className="px-4 py-3">
                  <Badge
                    tone={
                      r.stage === 'closed_lost'
                        ? 'danger'
                        : r.stage === 'closed_won'
                        ? 'success'
                        : 'neutral'
                    }
                    className="font-medium inline-flex w-fit"
                  >
                    {r.stage === 'closed_lost'
                      ? (() => {
                          const reasonLower = (
                            (typeof r.lostReasonId === 'object' && (r.lostReasonId as any)?.name) ||
                            (r as any).lostReasonName ||
                            r.lostReasonNote ||
                            ''
                          ).toLowerCase();
                          if (reasonLower.includes('price')) return 'Closed Lost: Price Issue';
                          if (reasonLower.includes('quality')) return 'Closed Lost: Poor Quality';
                          if (typeof r.lostReasonId === 'object' && (r.lostReasonId as any)?.name) return `Closed Lost: ${(r.lostReasonId as any).name}`;
                          if ((r as any).lostReasonName) return `Closed Lost: ${(r as any).lostReasonName}`;
                          if (r.lostReasonNote) return `Closed Lost: ${r.lostReasonNote}`;
                          return 'Closed Lost';
                        })()
                      : stageLabel[r.stage] || r.stage}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-on-surface-variant font-medium">
                  {userName(r.assigned_to ?? r.user_id)}
                </td>
                <td className="px-4 py-3">
                  {r.source === 'indiamart' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-data-xs font-bold text-orange-700 border border-orange-200">
                      IndiaMART
                    </span>
                  ) : (
                    <span className="text-on-surface-variant capitalize">
                      {r.source || 'direct'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-body-xs text-on-surface-variant">
                  {r.phone && <div>{r.phone}</div>}
                  {r.email && <div className="text-on-surface-variant/70">{r.email}</div>}
                  {!r.phone && !r.email && '—'}
                </td>
                <td className="px-4 py-3 text-right font-bold tabular-nums text-on-surface">
                  {r.value ? formatMoney(r.value, r.currency) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A record several BDMs share (Incoming Leads → multiple BDMs): the record
 *  page says who is on it and who last worked on it. */
function SharedChip({ record }: { record: PipelineRecord }) {
  const n = Array.isArray(record.co_assignees) ? record.co_assignees.length : 0;
  if (!n) return null;
  return (
    <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary-container px-2 py-0.5 text-data-xs font-bold text-primary border border-indigo-200"
      title="Shared lead — open it to see who is working on it">
      <Users className="h-3 w-3" /> Shared · {n + 1} BDMs
    </span>
  );
}

function IconToggle({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'flex items-center gap-1.5 rounded-lg px-3 py-1.5 transition',
        active
          ? 'bg-primary text-on-primary font-semibold shadow-xs'
          : 'text-on-surface-variant hover:bg-surface-variant hover:text-on-surface',
      )}
    >
      {children}
    </button>
  );
}

export default SalesPipelinePage;
